import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

/**
 * Dark "night" glass-panel treatment shared by every floating surface in
 * the studio (top bar, side panels, toolbar, viewbar, tour bar, palette).
 * Reuses the design system's `--ds-night-*` tokens (see
 * design-system/css/variables.css) rather than a one-off dark theme.
 */
export const GlassPanel = styled(Box)({
  background: 'rgba(19, 18, 25, 0.86)',
  border: '1px solid var(--ds-night-border)',
  borderRadius: 14,
  backdropFilter: 'blur(20px) saturate(1.3)',
  WebkitBackdropFilter: 'blur(20px) saturate(1.3)',
  boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
  color: 'var(--ds-night-ink)',
})

export const STUDIO_ACCENT = {
  violet: '#be9dfd',
  gold: '#ffcf7d',
  coral: '#fea67d',
  cyan: '#01dafe',
}

export const monoLabelSx = {
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  letterSpacing: '0.15em',
  color: 'var(--ds-night-ink-subtle)',
  textTransform: 'uppercase' as const,
}

/**
 * Curved "reset to default" arrow — shared by every quick-reset affordance
 * in the studio (Pan's Recenter, Zoom's ↺) so they all read as the same
 * glyph rather than drifting into slightly different icons per control. An
 * SVG rather than a Unicode arrow character, which renders blank/
 * inconsistent across system font stacks.
 */
export function ResetGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox='0 0 18 18' fill='none' aria-hidden>
      <path d='M9 3.25a5.75 5.75 0 1 1-4.1 1.7' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
      <path d='M4.5 2.5v3.2h3.2' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  )
}

/**
 * Compact pill-chip segmented control — 1:1 with the mockup's `.seg button`.
 * Deliberately NOT the design system's
 * `SegmentedOptionGroup` (that's a larger radio-card style with descriptions,
 * meant for lighter contexts) — this studio needs the tighter Figma-toolbar
 * chip look everywhere the mockup uses `.seg` (FRAME profile, SCENE preset).
 */
export const SegRow = styled(Box)({ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 })

export const SegBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  height: 25,
  padding: '0 11px',
  borderRadius: 7,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  color: $on ? STUDIO_ACCENT.violet : 'var(--ds-night-ink-muted)',
  background: $on ? 'rgba(190,157,253,0.16)' : 'rgba(244,243,246,0.05)',
  border: `1px solid ${$on ? 'rgba(190,157,253,0.45)' : 'var(--ds-night-border)'}`,
  transition: 'all 140ms ease',
  '&:hover': { color: $on ? STUDIO_ACCENT.violet : 'var(--ds-night-ink)' },
}))

export const SegPro = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 6.5,
  letterSpacing: '0.1em',
  color: STUDIO_ACCENT.gold,
  marginLeft: 5,
  verticalAlign: 2,
})

export interface SegChipOption {
  value: string
  label: string
  pro?: boolean
}

export function SegChips({
  options,
  value,
  onChange,
}: {
  options: SegChipOption[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <SegRow role='radiogroup'>
      {options.map((o) => (
        <SegBtn key={o.value} type='button' role='radio' aria-checked={o.value === value} $on={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.pro && <SegPro>PRO</SegPro>}
        </SegBtn>
      ))}
    </SegRow>
  )
}

/**
 * Single-row labelled slider — 1:1 with the mockup's `.sld` (native
 * `<input type=range>`, custom track/thumb via CSS). Deliberately NOT the
 * design system's `SliderControl` — that one stacks label+value ABOVE the
 * track (two rows) and uses light-theme `--ds-ink*` tokens (near-black text,
 * near-invisible on this dark glass), plus MUI's Slider always paints a
 * colored "filled" segment the mockup's flat single-tone track doesn't have.
 */
const SliderRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 })

const SliderLabel = styled('span')({ width: 52, flexShrink: 0, fontSize: 11, color: 'var(--ds-night-ink-muted)' })

const SliderValue = styled('span')({
  width: 42,
  flexShrink: 0,
  textAlign: 'right',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  color: 'var(--ds-night-ink-muted)',
})

const thumbSx = {
  width: 13,
  height: 13,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 5px rgba(0,0,0,0.5), 0 0 0 3px rgba(190,157,253,0.28)',
}

const RangeInput = styled('input')({
  flex: 1,
  // Flex items default to `min-width: auto`, which for a range input
  // resolves to its UA-stylesheet intrinsic width (~129px in Chrome) rather
  // than 0 — in this panel's ~234px-wide slider row (52px label + 42px value
  // + gaps already claim ~112px) that intrinsic minimum silently overflowed
  // the row by ~10px, producing a horizontal scrollbar on the whole
  // right-side inspector (`Scroll`'s `overflowY: auto` computes its
  // `overflow-x` to `auto` too per the CSS spec, so it materializes there).
  minWidth: 0,
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  height: 22,
  background: 'none',
  cursor: 'pointer',
  '&::-webkit-slider-runnable-track': { height: 3, borderRadius: 2, background: 'rgba(244,243,246,0.14)' },
  '&::-webkit-slider-thumb': { WebkitAppearance: 'none', marginTop: -5, ...thumbSx },
  '&::-moz-range-track': { height: 3, borderRadius: 2, background: 'rgba(244,243,246,0.14)' },
  '&::-moz-range-thumb': { border: 'none', ...thumbSx },
})

export interface StudioSliderProps {
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onChange: (value: number) => void
  /** Fired on pointer-up / change-commit — use for undo history (not every drag tick). */
  onCommit?: (value: number) => void
  valueFormatter?: (value: number) => string
  sx?: BoxProps['sx']
}

export function StudioSlider({
  label,
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  onCommit,
  valueFormatter,
  sx,
}: StudioSliderProps) {
  return (
    <SliderRow sx={sx}>
      <SliderLabel>{label}</SliderLabel>
      <RangeInput
        type='range'
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onPointerUp={(e) => onCommit?.(parseFloat((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
            onCommit?.(parseFloat((e.target as HTMLInputElement).value))
          }
        }}
      />
      <SliderValue>{valueFormatter ? valueFormatter(value) : Math.round(value)}</SliderValue>
    </SliderRow>
  )
}

/**
 * Rounded-square color swatch — 1:1 with the mockup's `.swb` (8px radius
 * square, light inset ring, violet glow when selected). Deliberately NOT the
 * design system's `ColorDot` — that one is a pill/circle with a dark
 * light-theme border and a `--ds-ink` (near-black) selection outline, neither
 * of which matches this dark studio's swatch treatment.
 */
const SwatchBtn = styled('button', { shouldForwardProp: (p) => p !== '$selected' && p !== '$size' })<{
  $selected?: boolean
  $size: number
}>(({ $selected, $size }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: $size,
  height: $size,
  borderRadius: 8,
  cursor: 'pointer',
  flexShrink: 0,
  position: 'relative',
  boxShadow: $selected
    ? `inset 0 0 0 1px rgba(255,255,255,0.2), 0 0 0 2px #131219, 0 0 0 3.5px ${STUDIO_ACCENT.violet}`
    : 'inset 0 0 0 1px rgba(255,255,255,0.14)',
  transition: 'transform 140ms ease, box-shadow 140ms ease',
  '&:hover': { transform: 'scale(1.12)' },
}))

export function Swatch({
  color,
  size = 24,
  selected,
  label,
  onClick,
}: {
  color: string
  size?: number
  selected?: boolean
  label?: string
  onClick?: () => void
}) {
  return (
    <SwatchBtn
      type='button'
      aria-label={label}
      aria-pressed={selected}
      $selected={selected}
      $size={size}
      style={{ background: color }}
      onClick={onClick}
    />
  )
}
